import { z } from "zod";
import { ColorSchemeIdSchema } from "./colorScheme";
import { FontSetIdSchema } from "./fontSet";
export const CommonColorSchema = z.object({
    background: z.string().default("blue-900"),
    foreground: z.string().default("sky-300"),
    cardBackground: z.string().default("blue-500"),
    cardForeground: z.string().default("sky-50"),
    buttonBackground: z.string().default("sky-50"),
    buttonForeground: z.string().default("sky-950"),
}).optional().default({
    background: "blue-900",
    foreground: "sky-300",
    cardBackground: "blue-500",
    cardForeground: "sky-50",
    buttonBackground: "sky-50",
    buttonForeground: "sky-950",
});
export const FontSetSchema = z.object({
    heading: z.string().default("Inter Bold"),
    subheading: z.string().default("Inter Regular"),
    body: z.string().default("Inter Regular"),
});
export const ImageSchema = z.object({
    src: z.string(),
    alt: z.string().optional(),
});
export const SiteDetailsSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    colorSchemeId: ColorSchemeIdSchema.optional(),
    customColors: z.array(z.string()).optional(),
    fontSetId: FontSetIdSchema.optional().default("modern"),
}).refine((data) => data.colorSchemeId || data.customColors, { message: "Either colorSchemeId or customColors must be provided" }).transform((data) => {
    // Provide defaults if neither is set (for backward compatibility)
    if (!data.colorSchemeId && !data.customColors) {
        return { ...data, colorSchemeId: "natural" };
    }
    return data;
});
export const LinkSchema = z.object({
    title: z.string().optional(),
    url: z.string().url(),
});
export const LinkWithDescriptionSchema = LinkSchema.extend({
    description: z.string().optional(),
});
export const LinkWithImageSchema = LinkSchema.extend({
    image: ImageSchema.optional(),
});
export const LinkWithDescriptionAndImageSchema = LinkWithDescriptionSchema.extend({
    image: ImageSchema.optional(),
});
export const SocialMediaPlatformSchema = z.enum([
    'email',
    'youtube',
    'instagram',
    'tiktok',
    'x', // Twitter/X
    'pinterest',
    'whatsapp',
    'facebook',
    'linkedin',
    'reddit',
    'discord',
    'telegram',
    'snapchat',
    'github',
    'gitlab',
    'twitch',
    'vimeo',
    'medium',
    'spotify',
    'soundcloud',
    'dribbble',
    'behance',
    'mastodon',
    'weixin', // WeChat
    'line',
    'vk',
]);
export const SocialMediaLinkSchema = z.object({
    platform: SocialMediaPlatformSchema,
    url: z.string(),
    displayName: z.string().optional(),
});
export const FaqSchema = z.object({
    question: z.string().optional(),
    answer: z.string().optional(),
});
export const FeatureSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    image: z.string().optional(), // Assuming image is a URL or path string
});
// Hero Cards
export const HeroCommonCardSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    brandTitle: z.string().optional(),
    logo: ImageSchema.optional(),
    button: LinkSchema.optional(),
    socialLinks: z.array(SocialMediaLinkSchema).optional().default([]),
    colors: CommonColorSchema,
    image: ImageSchema.optional(),
    backgroundImage: ImageSchema.optional(),
    diagonalAngle: z.number().min(10).max(45).default(20).optional(), // Used by Hero5
});
export const Hero1CardSchema = HeroCommonCardSchema.extend({
    type: z.literal("hero1"),
});
export const Hero2CardSchema = HeroCommonCardSchema.extend({
    type: z.literal("hero2"),
});
export const Hero3CardSchema = HeroCommonCardSchema.extend({
    type: z.literal("hero3"),
});
export const Hero4CardSchema = HeroCommonCardSchema.extend({
    type: z.literal("hero4"),
});
export const Hero5CardSchema = HeroCommonCardSchema.extend({
    type: z.literal("hero5"),
});
export const HeroCardSchema = z.union([Hero1CardSchema, Hero2CardSchema, Hero3CardSchema, Hero4CardSchema, Hero5CardSchema]);
// Link Cards
export const LinkCommonCardSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    imageSize: z.number().optional(),
    colors: CommonColorSchema,
});
export const Link1CardSchema = LinkCommonCardSchema.extend({
    type: z.literal("link1"),
    links: z.array(LinkSchema),
});
export const Link2CardSchema = LinkCommonCardSchema.extend({
    type: z.literal("link2"),
    links: z.array(LinkSchema),
});
export const Link3CardSchema = LinkCommonCardSchema.extend({
    type: z.literal("link3"),
    links: z.array(LinkWithImageSchema),
});
export const Link4CardSchema = LinkCommonCardSchema.extend({
    type: z.literal("link4"),
    links: z.array(LinkWithDescriptionAndImageSchema),
});
export const Link5CardSchema = LinkCommonCardSchema.extend({
    type: z.literal("link5"),
    links: z.array(LinkWithDescriptionAndImageSchema),
});
export const Link6CardSchema = LinkCommonCardSchema.extend({
    type: z.literal("link6"),
    links: z.array(LinkWithDescriptionAndImageSchema),
});
export const LinkCardSchema = z.union([
    Link1CardSchema,
    Link2CardSchema,
    Link3CardSchema,
    Link4CardSchema,
    Link5CardSchema,
    Link6CardSchema,
]);
// Image Cards - based on Link cards but with captions instead of clickable links
export const ImageWithCaptionSchema = z.object({
    image: ImageSchema,
    caption: z.string().optional(),
});
export const ImageCommonCardSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    colors: CommonColorSchema,
});
export const Image1CardSchema = ImageCommonCardSchema.extend({
    type: z.literal("image1"),
    images: z.array(ImageWithCaptionSchema),
});
export const Image2CardSchema = ImageCommonCardSchema.extend({
    type: z.literal("image2"),
    images: z.array(ImageWithCaptionSchema),
});
export const Image3CardSchema = ImageCommonCardSchema.extend({
    type: z.literal("image3"),
    images: z.array(ImageWithCaptionSchema),
});
export const Image4CardSchema = ImageCommonCardSchema.extend({
    type: z.literal("image4"),
    images: z.array(ImageWithCaptionSchema),
});
export const Image5CardSchema = ImageCommonCardSchema.extend({
    type: z.literal("image5"),
    images: z.array(ImageWithCaptionSchema),
});
export const ImageCardSchema = z.union([
    Image1CardSchema,
    Image2CardSchema,
    Image3CardSchema,
    Image4CardSchema,
    Image5CardSchema,
]);
// Feature Cards
export const FeatureCommonCardSchema = z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    features: z.array(FeatureSchema),
});
export const Feature1CardSchema = FeatureCommonCardSchema.extend({
    type: z.literal("feature1"),
    colors: CommonColorSchema,
});
export const Feature2CardSchema = FeatureCommonCardSchema.extend({
    type: z.literal("feature2"),
    colors: CommonColorSchema,
});
export const FeatureCardSchema = z.union([
    Feature1CardSchema,
    Feature2CardSchema,
]);
// Text Cards
export const TextCommonCardSchema = z.object({
    title: z.string().optional(),
    subtitle: z.string().optional(),
    colors: CommonColorSchema,
});
export const Text1CardSchema = TextCommonCardSchema.extend({
    type: z.literal("text1"),
});
// Faq Cards
export const FaqCommonCardSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    faqs: z.array(FaqSchema),
    expandAll: z.boolean().optional(),
    colors: CommonColorSchema,
});
export const Faq1CardSchema = FaqCommonCardSchema.extend({
    type: z.literal("faq1"),
});
export const Faq2CardSchema = FaqCommonCardSchema.extend({
    type: z.literal("faq2"),
});
export const FaqCardSchema = z.union([
    Faq1CardSchema,
    Faq2CardSchema,
]);
// WhatsApp Cards
export const WhatsappCommonCardSchema = z.object({
    whatsappNumber: z.string().regex(/^\+[1-9]\d{1,14}$/, "Must be a valid international phone number starting with +"),
    initialMessage: z.string().optional(),
    buttonText: z.string().optional().default("Start WhatsApp Chat"),
    title: z.string().optional(),
    description: z.string().optional(),
    colors: CommonColorSchema,
});
export const Whatsapp1CardSchema = WhatsappCommonCardSchema.extend({
    type: z.literal("whatsapp1"),
});
export const Whatsapp2CardSchema = WhatsappCommonCardSchema.extend({
    type: z.literal("whatsapp2"),
});
export const WhatsappCardSchema = z.union([
    Whatsapp1CardSchema,
    Whatsapp2CardSchema,
]);
// Tipping Cards
export const PaymentMethodSchema = z.enum(["paynow"]);
export const PayeeInfoSchema = z.object({
    paynow: z.object({
        type: z.enum(["MOBILE", "UEN"]),
        value: z.string(),
        recipientName: z.string().optional(),
    }).optional(),
});
export const TippingCommonCardSchema = z.object({
    title: z.string(),
    description: z.string().optional(),
    paymentMethod: PaymentMethodSchema,
    payeeInfo: PayeeInfoSchema,
    amounts: z.array(z.number()).default([5, 10, 20, 50]),
    allowCustomAmount: z.boolean().default(true),
    minAmount: z.number().optional().default(1),
    maxAmount: z.number().optional().default(999),
    currency: z.string().default("SGD"),
    buttonText: z.string().default("Generate QR Code"),
    thankYouNote: z.string().default("Thank you for your support! 🙏"),
    colors: CommonColorSchema,
});
export const Tipping1CardSchema = TippingCommonCardSchema.extend({
    type: z.literal("tipping1"),
});
export const Tipping2CardSchema = TippingCommonCardSchema.extend({
    type: z.literal("tipping2"),
});
export const TippingCardSchema = z.union([
    Tipping1CardSchema,
    Tipping2CardSchema,
]);
// Footer Cards
export const Footer1CardSchema = z.object({
    type: z.literal("footer1"),
    colors: CommonColorSchema,
});
// Card Union
export const CardSchema = z.union([
    HeroCardSchema,
    LinkCardSchema,
    ImageCardSchema,
    FeatureCardSchema, // Now includes Feature1 and Feature2
    Text1CardSchema, // Direct use, as TextCard is an alias
    FaqCardSchema, // Now includes Faq1 and Faq2
    WhatsappCardSchema,
    TippingCardSchema,
    Footer1CardSchema, // Direct use, as FooterCard is an alias
]);
// Page
export const PageSchema = z.object({
    path: z.string(),
    title: z.string().optional(),
    cards: z.array(CardSchema),
});
// Config
// Custom Domain
export const CustomDomainValidationRecordsSchema = z.object({
    txt: z.object({
        name: z.string(),
        value: z.string(),
    }),
    ssl: z.object({
        txt_name: z.string(),
        txt_value: z.string(),
    }).optional(),
});
export const CustomDomainConfigSchema = z.object({
    domain: z.string(),
    cloudflareHostnameId: z.string(),
    validationRecords: CustomDomainValidationRecordsSchema.optional(),
});
// Config
export const ConfigSchema = z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    favicon: ImageSchema.optional(),
    fontSetId: FontSetIdSchema.optional().default("modern"),
    gtag: z.string().optional(),
    pages: z.array(PageSchema),
});
